import { createSlice } from "@reduxjs/toolkit";
import { Dispatch } from "redux";
import axios from "../utils/axios.tsx";
import {
  ConversationState,
  Exchange,
  ExchangeState,
  ConversationGroups,
  RootState,
} from "./types.tsx";

function isLaterThanNumberOfDaysInThePast(
  date: Date,
  numberOfDaysInThePast: number,
) {
  var cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - numberOfDaysInThePast);
  return cutoffDate < date;
}

function isEarlierThanNumberOfDaysInThePast(
  date: Date,
  numberOfDaysInThePast: number,
) {
  var cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - numberOfDaysInThePast);
  return cutoffDate >= date;
}

const initialState: ConversationState = {
  exchangesByConversation: {},
  conversations: {
    pinned: [],
    last_7_days: [],
    last_30_days: [],
    other: [],
  },
  selectedConversation: "",
  availableModels: [],
  selectedModel: "",
  userStoppedStream: false,
};

function parseDate(str: string): Date {
  return new Date(str.split(".")[0]);
}

function generateConversations(action: {
  payload: any;
  type: string;
}): ConversationGroups {
  var conversationGroups: ConversationGroups = {
    last_30_days: [],
    last_7_days: [],
    other: [],
    pinned: [],
  };
  for (var item of action.payload) {
    if (item.is_pinned) {
      conversationGroups.pinned.push({
        id: item.id,
        title: item.title,
        summary: item.summary,
        is_pinned: item.is_pinned,
        created_at: item.created_at,
      });
    } else if (
      isLaterThanNumberOfDaysInThePast(parseDate(item.created_at), 7)
    ) {
      conversationGroups.last_7_days.push({
        id: item.id,
        title: item.title,
        summary: item.summary,
        is_pinned: item.is_pinned,
        created_at: item.created_at,
      });
    } else if (
      isEarlierThanNumberOfDaysInThePast(parseDate(item.created_at), 7) &&
      isLaterThanNumberOfDaysInThePast(parseDate(item.created_at), 30)
    ) {
      conversationGroups.last_30_days.push({
        id: item.id,
        title: item.title,
        summary: item.summary,
        is_pinned: item.is_pinned,
        created_at: item.created_at,
      });
    } else {
      conversationGroups.other.push({
        id: item.id,
        title: item.title,
        summary: item.summary,
        is_pinned: item.is_pinned,
        created_at: item.created_at,
      });
    }
  }

  return conversationGroups;
}

export const getSelectedConversationExchanges = (
  state: RootState,
): ExchangeState | undefined => {
  const selectedId = state.conversationState.selectedConversation;
  return state.conversationState.exchangesByConversation[selectedId];
};

const conversationSlice = createSlice({
  name: "conversation",
  initialState,
  reducers: {
    setSelectedConversation(state, action) {
      let conversationId = action.payload;
      state.selectedConversation = conversationId;
      if (!state.exchangesByConversation[conversationId]) {
        state.exchangesByConversation[conversationId] = {
          byId: {},
          allIds: [],
        };
      }
    },
    resetSelectedConversation(state) {
      state.selectedConversation = "";
    },
    loadAllExchanges(state, action) {
      const conversationId = action.payload.conversationId;

      // Ensure the conversation entry exists
      if (!state.exchangesByConversation[conversationId]) {
        state.exchangesByConversation[conversationId] = {
          byId: {},
          allIds: [],
        };
      }

      const current = state.exchangesByConversation[conversationId];

      action.payload.exchanges.forEach((exchange: Exchange) => {
        if (!current.byId[exchange.id]) {
          current.byId[exchange.id] = exchange;
          current.allIds.push(exchange.id);
        }
      });
    },
    resetExchanges(state) {
      if (state.selectedConversation) {
        state.exchangesByConversation[state.selectedConversation] = {
          byId: {},
          allIds: [],
        };
      }
    },
    appendExchange(state, action) {
      const conversationId =
        action.payload.conversation_id || state.selectedConversation;

      if (
        action.payload.conversation_id &&
        state.exchangesByConversation["new"] &&
        state.selectedConversation == "new"
      ) {
        state.exchangesByConversation[conversationId] =
          state.exchangesByConversation["new"];
        state.selectedConversation = conversationId;

        delete state.exchangesByConversation["new"];
      }
      if (!state.exchangesByConversation[conversationId]) {
        state.exchangesByConversation[conversationId] = {
          byId: {},
          allIds: [],
        };
      }

      if (action.payload.response === null) {
        const newId =
          action.payload.id !== undefined
            ? action.payload.id
            : `last-exchange-temp-${state.exchangesByConversation[conversationId].allIds.length}`;
        state.exchangesByConversation[conversationId].allIds.push(newId);
        state.exchangesByConversation[conversationId].byId[newId] = {
          id: newId,
          conversation_id: conversationId,
          prompt: action.payload.prompt,
          response: "",
          json_response: null,
          feedback: null,
          user_comment: null,
          progress_update: false,
        };
      } else {
        const exchanges = state.exchangesByConversation[conversationId];
        const lastExchangeId = exchanges.allIds[exchanges.allIds.length - 1];
        let lastExchange = exchanges.byId[lastExchangeId];
        lastExchange.progress_update = action.payload.progress_update;
        lastExchange.response = action.payload.response;
        lastExchange.type = action.payload.type;
        lastExchange.json_response = {
          content: action.payload.response,
          type: action.payload.type,
        };
        lastExchange.feedback = action.payload.feedback;
        lastExchange.user_comment = action.payload.user_comment;
        if (
          String(lastExchangeId).startsWith("last-exchange-temp") &&
          action.payload.id
        ) {
          exchanges.byId[action.payload.id] = exchanges.byId[lastExchangeId];
          delete exchanges.byId[lastExchangeId];
          exchanges.allIds[exchanges.allIds.length - 1] = action.payload.id;
        }
      }
    },
    appendCustomActionResponse(state, action) {
      /*
      Used for Save Favourite and Save Memories.
      As these are not true question/response exchanges and don't have a unique id
      originating from the DB, we set the negative value of the next exchange cast as string.
      If exchanges.length == 10, the new id will be "-11"
      */
      const conversationId =
        action.payload.conversation_id || state.selectedConversation;
      if (!conversationId) return;

      if (!state.exchangesByConversation[conversationId]) {
        state.exchangesByConversation[conversationId] = {
          byId: {},
          allIds: [],
        };
      }

      const exchanges = state.exchangesByConversation[conversationId];
      const lastExchangeId = exchanges.allIds[exchanges.allIds.length - 1];
      const lastExchange = lastExchangeId
        ? exchanges.byId[lastExchangeId]
        : undefined;

      // If the last exchange is a custom prompt with no response yet, update it in place
      if (
        lastExchange &&
        typeof lastExchange.response === "string" &&
        lastExchange.response.trim() === "" &&
        typeof lastExchange.prompt === "string" &&
        lastExchange.prompt.trim() !== ""
      ) {
        lastExchange.response = action.payload.text;
        lastExchange.type = action.payload.type;
        lastExchange.json_response = {
          content: action.payload.text,
          type: action.payload.type,
          isStreaming: action.payload.isStreaming || false,
        };
      } else {
        const newId = String(-(exchanges.allIds.length - 1));
        exchanges.allIds.push(newId);
        exchanges.byId[newId] = {
          id: newId,
          conversation_id: conversationId,
          prompt: action.payload.savePrompt ? action.payload.prompt : "",
          response: action.payload.text,
          json_response: {
            content: action.payload.text,
            type: action.payload.type,
            isStreaming: action.payload.isStreaming || false,
          },
        };
      }
    },
    appendCustomActionPrompt(state, action) {
      /*
      As these are not true question/response prompts and don't have a unique id
      originating from the DB, we set the negative value of the next exchange cast as string.
      If exchanges.length == 10, the new id will be "-11"
      */
      const conversationId =
        action.payload.conversation_id || state.selectedConversation;
      if (!conversationId) return;

      if (!state.exchangesByConversation[conversationId]) {
        state.exchangesByConversation[conversationId] = {
          byId: {},
          allIds: [],
        };
      }

      const exchanges = state.exchangesByConversation[conversationId];
      const newId = String(-(exchanges.allIds.length - 1));
      exchanges.allIds.push(newId);
      exchanges.byId[newId] = {
        id: newId,
        conversation_id: conversationId,
        prompt: action.payload.text,
        response: "",
        json_response: {
          content: "",
          type: "",
        },
      };
    },
    updateExchangeById: (state, action) => {
      const { id, updateFields, conversationId } = action.payload;
      if (!state.exchangesByConversation[conversationId]) return;

      state.exchangesByConversation[conversationId].byId[id] = {
        ...state.exchangesByConversation[conversationId].byId[id],
        ...updateFields,
      };
    },
    loadAllConversations(state, action) {
      state.conversations = generateConversations(action);
    },
    setSelectedModel(state, action) {
      state.selectedModel = action.payload;
    },
    setAvailableModels(state, action) {
      const { availableModels, defaultModel } = action.payload;
      state.availableModels = availableModels;
      state.selectedModel = defaultModel;
    },
    setUserStoppedStream(state, action) {
      state.userStoppedStream = action.payload;
    },
  },
});

export const conversationActions = conversationSlice.actions;

export const appendCustomTextResponse = (text: string) => {
  return conversationActions.appendCustomActionResponse({
    text: text,
    type: "TextResponse",
  });
};

export const appendStreamingTextResponse = (
  text: string,
  isStreaming: boolean = false,
) => {
  return conversationActions.appendCustomActionResponse({
    text: text,
    type: "StreamingTextResponse",
    isStreaming: isStreaming,
  });
};

export const appendSignUpOptionsResponse = (
  text: string = "",
  isStreaming: boolean = true,
) => {
  return conversationActions.appendCustomActionResponse({
    text: text,
    type: "SignUpOptions",
    isStreaming: isStreaming,
  });
};

export const fetchConversations = () => {
  return async (dispatch: Dispatch) => {
    const sendRequest = async () => {
      const response = await axios.get("/v1/conversation/", {
        withCredentials: true,
      });

      if (response.status !== 200) {
        console.log("Error fetching conversations");
      } else {
        dispatch(
          conversationActions.loadAllConversations(
            response.data.objects as ConversationGroups,
          ),
        );
      }
    };

    try {
      await sendRequest();
    } catch (error) {
      console.error("Error fetching conversations:", error);
    }
  };
};

export const deleteConversation = (id: string) => {
  return async (dispatch: Dispatch) => {
    const sendRequest = async () => {
      const response = await axios.patch(`/v1/conversation/delete/${id}/`, {
        withCredentials: true,
      });

      if (response.status !== 200) {
        console.log("Error deleting conversation");
      } else {
        dispatch(fetchConversations() as any);
        dispatch(conversationActions.resetSelectedConversation());
      }
    };

    try {
      await sendRequest();
    } catch (error) {
      console.error("Error deleting conversation:", error);
    }
  };
};

export const patchConversation = (id: string, body: string) => {
  return async (dispatch: Dispatch) => {
    const sendRequest = async () => {
      const response = await axios.patch(`/v1/conversation/${id}/`, body, {
        withCredentials: true,
      });

      if (response.status !== 200) {
        console.log("Error patching conversation:", response);
      } else {
        dispatch(fetchConversations() as any);
      }
    };

    try {
      await sendRequest();
    } catch (error) {
      console.error("Error patching conversation:", error);
    }
  };
};

export const addConversation = () => {
  return async (dispatch: Dispatch) => {
    const sendRequest = async () => {
      const response = await axios.post("/v1/conversation/", {
        withCredentials: true,
      });

      if (response.status !== 201) {
        console.log("Error creating conversation:", response);
      } else {
        dispatch(fetchConversations() as any);
        dispatch(conversationActions.resetExchanges());
        dispatch(conversationActions.resetSelectedConversation());
        dispatch(conversationActions.setSelectedConversation(response.data.id));
      }
    };

    try {
      await sendRequest();
    } catch (error) {
      console.error("Error creating conversation:", error);
    }
  };
};

export const selectConversationAction = (conversation_id: string) => {
  return async (dispatch: Dispatch) => {
    dispatch(conversationActions.setSelectedConversation(conversation_id));
  };
};

export const fetchExchanges = (conversation_id?: string) => {
  return async (dispatch: Dispatch) => {
    const sendRequest = async () => {
      const response = await axios.get(
        `/v1/exchange/?conversation_identifier=${conversation_id}`,
        {
          withCredentials: true,
        },
      );
      if (response.status !== 200) {
      } else {
        dispatch(
          conversationActions.loadAllExchanges({
            conversationId: conversation_id,
            exchanges: response.data.objects as Exchange[],
          }),
        );
      }
    };
    try {
      await sendRequest();
    } catch (error) {
      console.error("Error fetching exchanges:", error);
    }
  };
};

export const fetchFavouriteQuestions = () => {
  return async (dispatch: Dispatch) => {
    const sendRequest = async () => {
      const response = await axios.get("/v1/favourite/");
      if (response.status !== 200) {
        console.log("Error fetching favourite questions");
      } else {
        const exchange = {
          response: response.data.objects,
          type: response.data.type,
          id: response.data.id,
        };
        dispatch(conversationActions.appendExchange(exchange as Exchange));
      }
    };
    try {
      await sendRequest();
    } catch (error) {
      console.error("Error fetching exchanges:", error);
    }
  };
};

export const fetchMemories = () => {
  return async (dispatch: Dispatch) => {
    const sendRequest = async () => {
      const response = await axios.get("/v1/memory/");
      if (response.status !== 200) {
        console.log("Error fetching memories");
      } else {
        const exchange = {
          response: response.data.objects,
          type: response.data.type,
          id: response.data.id,
        };
        dispatch(conversationActions.appendExchange(exchange as Exchange));
      }
    };
    try {
      await sendRequest();
    } catch (error) {
      console.error("Error fetching memories:", error);
    }
  };
};

export const fetchSynchronizationStatus = () => {
  return async (dispatch: Dispatch) => {
    const sendRequest = async () => {
      const response = await axios.get("/v1/integrations/sync_status/");
      if (response.status !== 200) {
        console.log("Error fetching synchronization status");
      } else {
        console.log("Synchronization status:", response.data);
        const exchange = {
          response: response.data.objects[0],
          type: response.data.type,
          id: response.data.id,
        };
        dispatch(conversationActions.appendExchange(exchange as Exchange));
      }
    };
    try {
      await sendRequest();
    } catch (error) {
      console.error("Error fetching synchronization status:", error);
    }
  };
};

export const fetchAvailableModels = () => {
  return async (dispatch: Dispatch) => {
    const sendRequest = async () => {
      const response = await axios.get("/v1/conversation/available-models/");
      if (response.status !== 200) {
        console.log("Error fetching available models");
      } else {
        dispatch(
          conversationActions.setAvailableModels({
            availableModels: response.data.available_models,
            defaultModel: response.data.default_model,
          }),
        );
      }
    };
    try {
      await sendRequest();
    } catch (error) {
      console.error("Error fetching exchanges:", error);
    }
  };
};

export default conversationSlice.reducer;

export const fetchSpreadsheets = () => {
  return async (dispatch: Dispatch) => {
    const sendRequest = async () => {
      const response = await axios.get("/v1/google-sheets/");
      if (response.status !== 200) {
        console.log("Error fetching spreadsheets");
      } else {
        const exchange = {
          response: response.data.objects,
          type: response.data.type,
          id: response.data.exchange_id,
        };
        dispatch(conversationActions.appendExchange(exchange as Exchange));
      }
    };
    try {
      await sendRequest();
    } catch (error) {
      console.error("Error fetching spreadsheets:", error);
    }
  };
};
